import { Bar } from '../../components/ui/Bar.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { anneeDe, dateComptaPour, exercicesAcompte } from '../../lib/cloture';
import { DEVISE, fmt } from '../../lib/currency';
import { tr, trf } from '../../lib/i18n';
import { compteDansLeCa, htDocument } from '../../lib/vat';
import { C } from '../../theme/palette';

/**
 * Compta → Synthèse : les acomptes qui touchent deux années, et l'année où
 * chacun compte.
 *
 * <p>Un acompte reçu en 2025 pour des travaux faits en 2026 peut compter en
 * 2025 (l'année où l'argent est arrivé, méthode des recettes) ou en 2026
 * (l'année des travaux : en 2025 il reste au bilan comme acompte reçu
 * d'avance). Les deux sont admises en Suisse ; le choix se faisait dans la
 * facture d'acompte, où la comptable ne le voyait pas. Il se fait aussi ici,
 * avec l'exercice sous les yeux.
 *
 * @param {object} props propriétés
 * @param {object[]} props.factures toutes les factures
 * @param {Function} [props.setFactures] met les factures à jour
 * @param {object[]} [props.clients] clients, pour leur nom
 * @param {object[]} [props.devis] offres, pour reconnaître l'acompte d'une offre
 * @param {number} props.annee exercice affiché
 * @returns {import('react').ReactElement|null} la carte, ou rien sans acompte concerné
 */
export function AcomptesAnnee({ factures, setFactures, clients, devis, annee }) {
  // L'argent doit être là : un acompte pas encore payé n'a pas d'année
  // d'encaissement. Et la question ne se pose plus quand la facture finale de
  // la même offre tombe la même année que l'acompte — ni pour une facture
  // d'avancement, qui suit les travaux.
  const offre = (f) => (devis || []).find((d) => d.id === f.devisId);
  const acompteDeLOffre = (f) =>
    !Array.isArray(devis) || String(offre(f)?.acompteFacture || '') === String(f.num || '');
  const finaleLaMemeAnnee = (f) =>
    f.devisId != null &&
    (factures || []).some(
      (x) =>
        x.id !== f.id &&
        x.devisId === f.devisId &&
        x.type !== 'acompte' &&
        x.type !== 'avoir' &&
        compteDansLeCa(x) &&
        anneeDe({ date: x.date }) === anneeDe({ date: f.date }),
    );
  const acomptes = (factures || []).filter((f) => {
    if (f.type !== 'acompte' || !compteDansLeCa(f)) return false;
    if (f.statut !== 'Payée' && f.statut !== 'Partielle') return false;
    if (!acompteDeLOffre(f) || (!f.dateCompta && finaleLaMemeAnnee(f))) return false;
    const { encaisse, travaux } = exercicesAcompte(f);
    return encaisse === annee || travaux === annee || anneeDe(f) === annee;
  });
  if (!acomptes.length) return null;

  const choisit = (f, an, encaisse) =>
    setFactures &&
    setFactures((p) =>
      p.map((x) => (x.id === f.id ? { ...x, dateCompta: dateComptaPour(an, encaisse) } : x)),
    );

  return (
    <Card style={{ marginBottom: 11 }}>
      <Bar
        icon="📅"
        title={tr('Acomptes : dans quelle année ?')}
        color={C.navy}
        sub={tr('Argent reçu une année, travaux faits l’année suivante')}
      />
      <div style={{ padding: 13 }}>
        <div style={{ fontSize: 9.5, color: C.muted, lineHeight: 1.5, marginBottom: 8 }}>
          {tr(
            'Encaissé : l’acompte compte l’année où l’argent est arrivé. Travaux : il compte l’année des travaux, et reste au bilan comme acompte reçu d’avance jusque-là. Les deux sont admis ; décide avec ta fiduciaire, et garde la même règle chaque année.',
          )}
        </div>
        {acomptes.map((f) => {
          const { encaisse, travaux, retenu } = exercicesAcompte(f);
          const client = (clients || []).find((c) => c.id === f.clientId);
          return (
            <div key={f.id} style={{ borderTop: '1px solid ' + C.border, padding: '8px 0' }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: C.ink }}>
                {trf('Acompte n° {0}', f.num || '')}
                {client ? ' · ' + (client.nom || client.name || '') : ''}
              </div>
              <div style={{ fontSize: 9.5, color: C.muted, marginBottom: 6 }}>
                {trf('{0} HT · du {1}', DEVISE + ' ' + fmt(htDocument(f)), f.date || '')}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[encaisse, travaux].map((an) => (
                  <button
                    type="button"
                    key={an}
                    aria-pressed={retenu === an}
                    onClick={() => choisit(f, an, encaisse)}
                    style={{
                      flex: 1,
                      padding: '9px 6px',
                      borderRadius: 9,
                      fontSize: 12,
                      fontWeight: 800,
                      cursor: 'pointer',
                      background: retenu === an ? C.navy : '#fff',
                      color: retenu === an ? '#fff' : C.ink,
                      border: '1px solid ' + (retenu === an ? C.navy : C.border),
                    }}
                  >
                    {an}
                    <div style={{ fontSize: 8, fontWeight: 600, opacity: 0.85 }}>
                      {an === encaisse ? tr('encaissé') : tr('travaux')}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
